import { createWeakMemoizedFactory } from './telemetry';

export interface FetchAjaxStubLogger {
  ajax: (...args: any[]) => any;
  trace: (...args: any[]) => any;
  error: (...args: any[]) => any;
}

export interface FetchTraceLogger {
  trace: (...args: any[]) => any;
}

export interface FetchInstrumentationSchema {
  readonly scenario: readonly {
    readonly name: string;
    readonly value: string;
    readonly activity: readonly {
      readonly name: string;
      readonly value: string;
    }[];
  }[];
}

export interface FetchRequestPayloadLoggers {
  requestLogger: FetchTraceLogger;
  responseLogger: FetchTraceLogger;
}

interface ActivityFactory<TLogger> {
  create: () => TLogger;
}

export interface FetchAjaxScenarioFactory {
  create: () => {
    ajaxStubActivity: ActivityFactory<FetchAjaxStubLogger>;
  };
}

export interface FetchRequestScenarioFactory {
  create: () => {
    RequestPayload: ActivityFactory<FetchTraceLogger>;
    Response: ActivityFactory<FetchTraceLogger>;
  };
}

export interface CreateFetchInstrumentationLoggersOptions<TInstrumentation extends object> {
  scenarioName: string;
  getAjaxScenario: (
    instrumentation: TInstrumentation
  ) => FetchAjaxScenarioFactory | undefined;
  getRequestScenario: (
    instrumentation: TInstrumentation
  ) => FetchRequestScenarioFactory | undefined;
  addScenario?: (
    instrumentation: TInstrumentation,
    schema: FetchInstrumentationSchema
  ) => void;
}

export interface FetchInstrumentationLoggerAccessors<TInstrumentation extends object> {
  getAjaxStubLogger: (
    instrumentation?: TInstrumentation
  ) => FetchAjaxStubLogger;
  getRequestPayloadLogger: (
    instrumentation?: TInstrumentation
  ) => FetchRequestPayloadLoggers;
}

const noop = (): void => undefined;

export const NOOP_AJAX_STUB_LOGGER: FetchAjaxStubLogger = {
  ajax: noop,
  trace: noop,
  error: noop,
};

export const NOOP_REQUEST_PAYLOAD_LOGGERS: FetchRequestPayloadLoggers = {
  requestLogger: { trace: noop },
  responseLogger: { trace: noop },
};

export function createFetchInstrumentationLoggers<TInstrumentation extends object>(options: CreateFetchInstrumentationLoggersOptions<TInstrumentation>): FetchInstrumentationLoggerAccessors<TInstrumentation> {
  const schema: FetchInstrumentationSchema = {
    scenario: [
      {
        name: options.scenarioName,
        value: options.scenarioName,
        activity: [
          { name: 'RequestPayload', value: 'RequestPayload' },
          { name: 'Response', value: 'Response' },
        ],
      },
    ],
  };
  const registrationAttempts = new WeakSet<TInstrumentation>();
  const getCachedAjaxStubLogger = createWeakMemoizedFactory((instrumentation: TInstrumentation) => {
    const scenario = options.getAjaxScenario(instrumentation)!;
    return scenario.create().ajaxStubActivity.create();
  });
  const getCachedRequestPayloadLogger = createWeakMemoizedFactory((instrumentation: TInstrumentation) => {
    const scenario = options.getRequestScenario(instrumentation)!.create();
    return {
      requestLogger: scenario.RequestPayload.create(),
      responseLogger: scenario.Response.create(),
    };
  });

  return {
    getAjaxStubLogger: (instrumentation) => {
      if (!instrumentation || !options.getAjaxScenario(instrumentation)) {
        return NOOP_AJAX_STUB_LOGGER;
      }
      return getCachedAjaxStubLogger(instrumentation);
    },
    getRequestPayloadLogger: (instrumentation) => {
      if (!instrumentation) {
        return NOOP_REQUEST_PAYLOAD_LOGGERS;
      }
      let scenario = options.getRequestScenario(instrumentation);
      if (!scenario && options.addScenario && !registrationAttempts.has(instrumentation)) {
        registrationAttempts.add(instrumentation);
        options.addScenario(instrumentation, schema);
        scenario = options.getRequestScenario(instrumentation);
      }
      if (!scenario) return NOOP_REQUEST_PAYLOAD_LOGGERS;
      return getCachedRequestPayloadLogger(instrumentation);
    },
  };
}
